import React, {useCallback, useMemo, useState} from 'react';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper
} from '@mui/material';
import {CompanyUser} from '@/types/user';
import {useTranslation} from 'react-i18next';
import {env} from '@/config';
import {useAppSelector} from '@/redux-store/store';
import {SortConfig, getComparisonResult} from './employee-utils';
import SearchBar from '@/components/ui/search-bar';
import EmployeeRow from './employee-row';
import EmployeeTableHeaderRow from './employee-table-header-row';
import GoBackComponent from '@/components/ui/go-back-component';
import RegistrationDetailsHider from '@/components/company/registration-details-hider';

const {SERVER} = env;

interface EmployeesListProps {
  employees: CompanyUser[];
  companyId: string;
  companiesDashboardMode?: boolean;
}

const EmployeesList: React.FC<EmployeesListProps> = ({
  employees,
  companiesDashboardMode,
  companyId
}) => {
  const {t} = useTranslation();

  const companyIdFromRedux = useAppSelector(
    (state) => state.company.company._id
  );

  const accessCodes = useAppSelector(
    (state) => state.company.company.accessCodes
  );

  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    primary: {
      key: 'confirmed',
      direction: 'asc'
    },
    secondary: {
      key: 'name',
      direction: 'asc'
    }
  });

  const registerLink = `${SERVER}/api/sjone/register/${companyIdFromRedux}`;

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const primaryComparison = getComparisonResult(
        a,
        b,
        sortConfig.primary.key,
        sortConfig.primary.direction,
        t
      );
      if (primaryComparison !== 0) return primaryComparison;

      if (sortConfig.secondary) {
        return getComparisonResult(
          a,
          b,
          sortConfig.secondary.key,
          sortConfig.secondary.direction,
          t
        );
      }

      if (sortConfig.tertiary) {
        return getComparisonResult(
          a,
          b,
          sortConfig.tertiary.key,
          sortConfig.tertiary.direction,
          t
        );
      }

      return 0;
    });
  }, [employees, sortConfig, t]);

  const filteredEmployees = useMemo(() => {
    if (!searchText) return sortedEmployees;
    return sortedEmployees.filter((employee) => {
      const nameMatches =
        employee.name &&
        employee.name.toLowerCase().includes(searchText.toLowerCase());

      const emailMatches =
        employee.email &&
        employee.email.toLowerCase().includes(searchText.toLowerCase());

      return nameMatches || emailMatches;
    });
  }, [searchText, sortedEmployees]);

  const requestSort = useCallback(
    (key: string) => {
      if (sortConfig.primary.key === key) {
        setSortConfig((prevConfig) => ({
          ...prevConfig,
          primary: {
            ...prevConfig.primary,
            direction: prevConfig.primary.direction === 'asc' ? 'desc' : 'asc'
          }
        }));
      } else if (sortConfig.secondary && sortConfig.secondary.key === key) {
        setSortConfig({
          primary: sortConfig.secondary,
          secondary: {
            key: key,
            direction: 'asc'
          },
          tertiary: sortConfig.primary
        });
      } else {
        setSortConfig({
          primary: {
            key: key,
            direction: 'asc'
          },
          secondary: sortConfig.primary,
          tertiary: sortConfig.secondary
        });
      }
    },
    [sortConfig]
  );

  const handleSearchChange = useCallback((searchTerm: string) => {
    setSearchText(searchTerm);
  }, []);

  return (
    <>
      <TableContainer component={Paper} style={{overflowX: 'auto'}}>
        {!companiesDashboardMode && <GoBackComponent />}

        <SearchBar
          onSearch={handleSearchChange}
          placeholder={t('employee_search')}
        />

        {!companiesDashboardMode && (
          <RegistrationDetailsHider
            registerLink={registerLink}
            accessCodes={accessCodes}
          />
        )}

        <Table>
          <TableHead>
            <EmployeeTableHeaderRow
              sortConfig={sortConfig}
              requestSort={requestSort}
            />
          </TableHead>

          <TableBody>
            {filteredEmployees.map((employee) => (
              <EmployeeRow
                key={employee._id}
                employee={employee}
                companyId={companyId}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default EmployeesList;
